import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { getSessionId } from 'owa-config';
import { fetchIdsFromMsGraph } from './fetchIdsFromMsGraph';
import type { TraceErrorObject } from 'owa-analytics-types';

import type { GraphUsersResponse } from '../types/GraphUsersResponse';

const GRAPH_GROUP_SIZE = 200;

export async function getMRIsFromGraphInBulk(smtps: string[]): Promise<GraphUsersResponse[]> {
    const mrisToReturn: GraphUsersResponse[] = [];
    const correlationId = getSessionId();

    /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
     * BASELINE: DO NOT COPY/PASTE!
     *	> Argument at index 1 for PerformanceDatapoint must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
    const performanceMarker = new PerformanceDatapoint('PresenceGetMrisFromGraphInBulk', undefined);
    performanceMarker.addCustomData({ correlationId, requestedCount: smtps.length });

    if (smtps) {
        // Break up the emails into groups of 200 to avoid hitting the max url length
        const groups = [];
        if (smtps.length > GRAPH_GROUP_SIZE) {
            for (let i = 0; i < smtps.length; i += GRAPH_GROUP_SIZE) {
                groups.push(smtps.slice(i, i + GRAPH_GROUP_SIZE));
            }
        } else {
            groups.push(smtps);
        }

        // Fire off requests for each group in parallel
        const responsePromises = [];
        for (const group of groups) {
            // Build string with all the smtps to search for this group
            const smtpsToQueryString = buildStringWithSmtpsToQuery(group);
            // If the smtpsToQueryString is over 10000 chars, we need to break it up into two groups
            if (smtpsToQueryString.length > 10000) {
                const half = Math.floor(group.length / 2);
                const firstHalf = group.slice(0, half);
                const secondHalf = group.slice(half);
                groups.push(firstHalf, secondHalf);
            } else {
                responsePromises.push(fetchIdsFromMsGraph(smtpsToQueryString, correlationId));
            }
        }

        // Wait for all the responses to come back and handle telemetry
        const results = await Promise.allSettled(responsePromises);
        let totalFulfilled = 0;
        let totalRejected = 0;
        let firstErrorStatus: number | undefined;
        const rejectionReasons: string[] = [];
        results.forEach(result => {
            if (result.status === 'fulfilled') {
                totalFulfilled++;
                const response = result.value;
                if (response) {
                    mrisToReturn.push(...response);
                }
            } else {
                totalRejected++;
                rejectionReasons.push(result.reason);
                if (firstErrorStatus === undefined) {
                    firstErrorStatus = result.reason?.status;
                }
            }
        });

        performanceMarker.addCustomData({
            fulfilledResponses: totalFulfilled,
            rejectedResponses: totalRejected,
            rejectionReasons: safeStringify(rejectionReasons),
        });

        if (totalRejected > 0) {
            const error = new Error(
                'Presence: Failed to fetch MRIs from Graph. Check MiscData.'
            ) as TraceErrorObject;

            error.httpStatus = firstErrorStatus ?? -1;

            performanceMarker.endWithError(DatapointStatus.ServerError, error);
        } else {
            performanceMarker.end();
        }
    }
    return mrisToReturn;
}

function buildStringWithSmtpsToQuery(smtps: string[]): string {
    const smtpsToQuery = smtps.map(smtp => `"${encodeURIComponent(smtp)}"`).join(',');
    return smtpsToQuery;
}

function safeStringify(input: string[]): string {
    try {
        return JSON.stringify(input);
    } catch (e) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS18046: 'e' is of type 'unknown'.
        // @ts-expect-error
        return e.message;
    }
}
